import { motion } from "framer-motion";
import { useEffect, useState } from "react";
import { useI18n } from "./i18n";
import closeEnvelope from "../../assets/Close.svg";
import envelopeBack from "../../assets/envelope_back.svg";
import envelopeFront from "../../assets/envelope_front.svg";
import invitationCard from "../../assets/invitation.png";
import seal from "../../assets/seal.svg";

export default function EnvelopeIntro({ onOpen, opened, reducedMotion }) {
  const { t } = useI18n();
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const mediaQuery = window.matchMedia("(max-width: 639px)");
    const handleChange = (event) => setIsMobile(event.matches);

    setIsMobile(mediaQuery.matches);
    mediaQuery.addEventListener("change", handleChange);

    return () => mediaQuery.removeEventListener("change", handleChange);
  }, []);

  const nameOffsetY = isMobile ? -56 : -125;
  const envelopeOffsetY = isMobile ? -36 : -10;
  const invitationOffsetY = isMobile ? "-30%" : "-33%";
  const invitationX = isMobile ? "-52%" : "-52%";
  const invitationWidth = isMobile ? "w-[70%]" : "w-[72%]";

  return (
    <div
      className={`relative w-full flex flex-col items-center justify-center overflow-hidden ${
        opened ? "h-[calc(100vh-3.5rem)]" : "h-screen"
      }`}
    >
      {/* Names - Stay in place when envelope opens */}
      <motion.div
        initial={false}
        animate={{ y: opened ? nameOffsetY : 0 }}
        transition={{ duration: reducedMotion ? 0.2 : 0.8, ease: "easeOut" }}
        className="absolute top-[18%] -translate-y-16 z-10 px-4 text-center pointer-events-none sm:top-[20%] sm:-translate-y-32"
      >
        <hgroup>
          <p className="uppercase">{opened ? t("app.couple.eyebrow_opened") : t("app.couple.eyebrow")}</p>
          <h1 className="font-script text-4xl sm:text-6xl text-theme-text">
            {t("app.couple.bride")} <span className="text-theme-text-faint">&</span>{" "}
            {t("app.couple.groom")}
          </h1>
        </hgroup>
      </motion.div>

      {/* Envelope Container - Sits closer to the header when opened */}
      <motion.div
        initial={false}
        className="relative w-full max-w-4xl aspect-[612/368] flex items-center justify-center overflow-visible"
        animate={{ y: opened ? envelopeOffsetY : 0 }}
        transition={{ duration: reducedMotion ? 0.2 : 0.8, ease: "easeOut" }}
      >
        {/* 1. Invitation Card - Placed inside the main container */}
        <motion.img
          src={invitationCard}
          alt="Wedding invitation card"
          initial={{ x: "-50%", y: "4%", opacity: 0 }}
          animate={{
            y: opened ? invitationOffsetY : "4%",
            opacity: opened ? 1 : 0,
            scale: opened ? 1 : 0.92,
            rotate: opened ? -5 : -2,
            x: invitationX,
          }}
          transition={{
            duration: reducedMotion ? 0.2 : 0.9,
            delay: reducedMotion ? 0 : 0.15,
            ease: "easeOut",
          }}
          className={`absolute left-1/2 top-[5%] z-20 object-contain pointer-events-none drop-shadow-xl sm:top-[5%] ${invitationWidth}`}
        />

        {/* 2. Close Envelope */}
        <motion.img
          src={closeEnvelope}
          alt="Closed envelope"
          initial={false}
          animate={{ opacity: opened ? 0 : 1 }}
          transition={{ duration: reducedMotion ? 0.2 : 0.6 }}
          className="absolute inset-0 z-30 h-full w-full object-contain"
        />

        {/* 3. Open Envelope Back Layer */}
        <motion.img
          src={envelopeBack}
          alt="Open envelope back"
          initial={false}
          animate={{ opacity: opened ? 1 : 0 }}
          transition={{ duration: reducedMotion ? 0.2 : 0.6 }}
          className="absolute inset-0 z-10 h-full w-full object-contain"
        />

        {/* 4. Open Envelope Front Layer */}
        <motion.img
          src={envelopeFront}
          alt="Open envelope front"
          initial={false}
          animate={{ opacity: opened ? 1 : 0 }}
          transition={{ duration: reducedMotion ? 0.2 : 0.6 }}
          className="absolute inset-0 z-30 h-full w-full object-contain"
        />

        {/* Seal Button - Only visible when not opened */}
        {!opened && (
          <motion.button
            onClick={onOpen}
            className="absolute bottom-[16%] left-[50%] z-30"
            initial={{ x: "-50%" }}
            whileHover={{ scale: 1.1, x: "-50%" }}
            whileTap={{ scale: 0.95, x: "-50%" }}
          >
            <img
              src={seal}
              alt="Click to open invitation"
              className="h-14 w-14 lg:h-32 lg:w-32 drop-shadow-lg"
            />
          </motion.button>
        )}
      </motion.div>

      {/* Scroll Down Indicator - Only shows after opening */}
      {opened && (
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.8, duration: reducedMotion ? 0.1 : 0.4 }}
          className="absolute bottom-6 z-20 text-center pointer-events-none"
        >
          <p className="text-xs uppercase tracking-wider text-theme-text-muted mb-1.5">
            {t("app.scroll_hint")}
          </p>
          <motion.div
            animate={{ y: [0, 6, 0] }}
            transition={{ duration: 2, repeat: Infinity }}
            className="flex justify-center"
          >
            <svg
              className="w-5 h-5 text-theme-text-faint"
              fill="none"
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth="2"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path d="M19 14l-7 7m0 0l-7-7m7 7V3"></path>
            </svg>
          </motion.div>
        </motion.div>
      )}
    </div>
  );
}
